/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../globalScope/GlobalScope';
import LoggerDefaults from '../logger/LoggerDefaults';
import LoggingLevelMapping from '../logger/LoggingLevelMapping';
import TelemetryDefault from '../metrics/TelemetryDefault';
import TelemetryLevelMapping from '../metrics/TelemetricLevelMapping';

export default class ConfigurationParameterReader {
  public static getStringValue(name: string): string {
    return ConfigurationParameterReader.readConfigurationParameterValue(name) ||
      ConfigurationParameterReader.readConfigurationParameterURIValue(`${name}-uri-parameter-name`) ||
      ConfigurationParameterReader.defaultStringValue[name];
  }

  public static getBooleanValue(name: string): boolean {
    const value = ConfigurationParameterReader.readConfigurationParameterValue(name);

    if (value) {
      return value === 'true' || value === '1' || value === 'On';
    }

    const valueFromUri = ConfigurationParameterReader.readConfigurationParameterURIValue(`${name}-uri-parameter-name`);

    if (valueFromUri) {
      return valueFromUri === 'true' || valueFromUri === '1' || valueFromUri === 'On';
    }

    return ConfigurationParameterReader.defaultBooleanValue[name] === true;
  }

  private static get defaultStringValue(): { [name: string]: string} {
    return {
      'phenix-metrics-level': TelemetryLevelMapping.convertTelemetryLevelToTelemetryLevelType(TelemetryDefault.defaultTelemetryLevel),
      'phenix-logging-level': LoggingLevelMapping.convertLoggingLevelToLoggingLevelType(LoggerDefaults.defaultLoggingLevel),
      'phenix-console-logging-level': LoggingLevelMapping.convertLoggingLevelToLoggingLevelType(LoggerDefaults.defaultConsoleLoggingLevel),
      'phenix-telemetry-logging-level': LoggingLevelMapping.convertLoggingLevelToLoggingLevelType(LoggerDefaults.defaultTelemetryLoggingLevel),
      'phenix-channel-token': '',
      'phenix-uri': '',
      'phenix-base-uri': ''
    };
  }

  private static get defaultBooleanValue(): { [name: string]: boolean } {
    return {
      'phenix-automatically-retry-on-failure': true,
      'phenix-automatically-reconnect-peer-connection': true,
      'phenix-force-garbage-collection-on-restart': true,
      'phenix-skip-garbage-collection-on-mobile-devices': true
    };
  }

  private static readConfigurationParameterValue(name: string): string | undefined {
    const metaValue = GlobalScope.value.documentQuerySelector(`meta[name='${name}']`);

    if (!metaValue) {
      return;
    }

    const value = metaValue.getAttribute('value');

    if (!value) {
      return;
    }

    return value;
  }

  private static readConfigurationParameterURIValue(name: string): string | undefined {
    const valueURIParameterName = ConfigurationParameterReader.readConfigurationParameterValue(name);

    if (!valueURIParameterName) {
      return;
    }

    const value = new GlobalScope.value.URLSearchParams(GlobalScope.value.locationSearch).get(valueURIParameterName) || undefined;

    if (!value) {
      return;
    }

    return value;
  }
}